import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  useTheme,
  alpha,
  Grid,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  Chip,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { createFlashcard, updateFlashcard, clearFlashcard } from "../../state/slices/flashcard";
import { getStudents } from "../../state/slices/tutor";
import { Add, Delete, Collections, ArrowBack, Save } from "@mui/icons-material";
import Loading from "../../components/loading/loading";
import ethiopiaCurriculum from "../../utils/ethiopiaCurriculum.json";

const CreateFlashcard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { currentFlashcard, loading, successMsg } = useSelector((state) => state.flashcard);
  
  const editingFlashcard = location.state?.flashcard;
  const user = useSelector((state) => state.user.user);
  const { students } = useSelector((state) => state.tutor);
  const isTutor = user?.roles?.includes("TUTOR");
  
  // Root navigation paths - don't show back button on these
  const rootPaths = ['/quiz/my', '/quiz/student', '/flashcard/my', '/flashcard/student', '/progress', '/', '/request', '/student-request', '/tutor-class-room', '/student-class-room'];
  const showBackButton = !rootPaths.includes(location.pathname);
  
  const [formData, setFormData] = useState({
    title: "",
    subject: "",
    gradeLevel: "",
    cards: [],
    assignedStudents: [],
  });
  
  const [availableSubjects, setAvailableSubjects] = useState([]);

  useEffect(() => {
    if (isTutor) {
      dispatch(getStudents());
    }
  }, [isTutor, dispatch]);

  useEffect(() => {
    if (editingFlashcard) {
      setFormData({
        title: editingFlashcard.title || "",
        subject: editingFlashcard.subject || "",
        gradeLevel: editingFlashcard.gradeLevel || "",
        cards: editingFlashcard.cards || [],
        assignedStudents: editingFlashcard.assignedStudents?.map(s => s._id || s) || [],
      });
    }
  }, [editingFlashcard]);

  useEffect(() => {
    if (formData.gradeLevel && ethiopiaCurriculum.subjects[formData.gradeLevel]) {
      setAvailableSubjects(ethiopiaCurriculum.subjects[formData.gradeLevel]);
    } else {
      setAvailableSubjects([]);
    }
  }, [formData.gradeLevel]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCardChange = (index, field, value) => {
    const newCards = [...formData.cards];
    newCards[index] = { ...newCards[index], [field]: value };
    setFormData({ ...formData, cards: newCards });
  };

  const addCard = () => {
    setFormData({
      ...formData,
      cards: [
        ...formData.cards,
        {
          front: "",
          back: "",
          hint: "",
        },
      ],
    });
  };

  const removeCard = (index) => {
    const newCards = formData.cards.filter((_, i) => i !== index);
    setFormData({ ...formData, cards: newCards });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Strip _id from cards
    const submitData = {
      ...formData,
      cards: formData.cards.map(c => {
        const { _id, ...card } = c;
        return card;
      })
    };
    
    if (editingFlashcard) {
      dispatch(updateFlashcard({ flashcardId: editingFlashcard._id, flashcardData: submitData })).then(() => {
        navigate("/flashcard/my");
      });
    } else {
      dispatch(createFlashcard(submitData)).then(() => {
        navigate("/flashcard/my");
      });
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        pb: 4,
      }}
    >
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Back Button - only show if not on root navigation page */}
        {showBackButton && (
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 3,
              color: 'text.secondary',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            {t('common.back')}
          </Button>
        )}

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: theme.palette.primary.main,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Collections sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 0.5 }}>
                {editingFlashcard ? t('flashcard.editFlashcardSet') : t('flashcard.createNewFlashcard')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {editingFlashcard ? t('flashcard.editFlashcardSetDesc') || 'Update your flashcard set details and cards' : t('flashcard.createNewFlashcardDesc') || 'Create a new flashcard set to help with studying'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          <Card 
            elevation={0}
            sx={{ 
              mb: 3,
              borderRadius: 3,
              bgcolor: 'white',
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('flashcard.flashcardSetTitle')}
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('flashcard.gradeLevel')}</InputLabel>
                    <Select
                      name="gradeLevel"
                      value={formData.gradeLevel}
                      onChange={handleChange}
                      label={t('flashcard.gradeLevel')}
                    >
                      {ethiopiaCurriculum.gradeLevels.map((grade) => (
                        <MenuItem key={grade} value={grade}>
                          {grade}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('common.subject')}</InputLabel>
                    <Select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      label={t('common.subject')}
                      disabled={!formData.gradeLevel}
                    >
                      {availableSubjects.map((subject) => (
                        <MenuItem key={subject} value={subject}>
                          {subject}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {isTutor && students && students.length > 0 && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>{t('flashcard.assignToStudents')}</InputLabel>
                      <Select
                        multiple
                        value={formData.assignedStudents}
                        onChange={(e) => setFormData({ ...formData, assignedStudents: e.target.value })}
                        label={t('flashcard.assignToStudents')}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((studentId) => {
                              const student = students.find(s => s.student._id === studentId || s.student.id === studentId);
                              return (
                                <Chip
                                  key={studentId}
                                  label={student ? `${student.student.firstName} ${student.student.lastName}` : studentId}
                                  size="small"
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {students.map((studentData) => {
                          const studentId = studentData.student._id || studentData.student.id;
                          return (
                            <MenuItem key={studentId} value={studentId}>
                              <Checkbox checked={formData.assignedStudents.includes(studentId)} />
                              <ListItemText primary={`${studentData.student.firstName} ${studentData.student.lastName}`} />
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 3,
              bgcolor: 'white',
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {t('flashcard.flashcards')}
                </Typography>
                <Button 
                  startIcon={<Add />} 
                  onClick={addCard}
                  variant="contained"
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    fontWeight: 600,
                  }}
                >
                  {t('flashcard.addCard')}
                </Button>
              </Box>

              {formData.cards.map((card, index) => (
                <Card 
                  key={index} 
                  elevation={0}
                  sx={{ 
                    mb: 2, 
                    p: 3,
                    borderRadius: 2,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                    <Typography variant="subtitle1">{t('flashcard.card')} {index + 1}</Typography>
                    <IconButton onClick={() => removeCard(index)} color="error">
                      <Delete />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('flashcard.frontLabel')}
                        value={card.front}
                        onChange={(e) => handleCardChange(index, "front", e.target.value)}
                        required
                        multiline
                        rows={2}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('flashcard.backLabel')}
                        value={card.back}
                        onChange={(e) => handleCardChange(index, "back", e.target.value)}
                        required
                        multiline
                        rows={2}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('flashcard.hintOptional')}
                        value={card.hint || ""}
                        onChange={(e) => handleCardChange(index, "hint", e.target.value)}
                        multiline
                        rows={1}
                      />
                    </Grid>
                  </Grid>
                </Card>
              ))}

              {formData.cards.length === 0 && (
                <Typography align="center" color="textSecondary" sx={{ py: 4 }}>
                  {t('flashcard.noCardsAdded')}
                </Typography>
              )}
            </CardContent>
          </Card>

          <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate("/flashcard/my")}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: 600,
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={formData.cards.length === 0}
              startIcon={<Save />}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              {editingFlashcard ? t('flashcard.updateFlashcardSet') : t('flashcard.createFlashcardSet')}
            </Button>
          </Box>
        </form>
      </Container>
    </Box>
  );
};

export default CreateFlashcard;

